// =============================================================================
// perfil-regiao-insights.ts — injecao dos cards de insight do Copiloto por aba.
//
// Proveniencia: Sections.perfilRegiao._ensureInsights (assets/unified-app.js 1090-1104)
// + UI.renderInsightCard (848-855, replicado FIEL aqui para nao acoplar ao global UI).
// Injeta 1 card no fim dos paineis eleicoes/demo/influencias/negocios, uma unica
// vez (flag insightsInjected no controller). O onclick do CTA chama sendPrompt via
// facade global window.sendPrompt — preservado como string inline.
// =============================================================================

/** Aba alvo + textos do card de insight, na ordem do objeto `data` original. */
interface PerfilInsightCardSpec {
  readonly panelKey: 'eleicoes' | 'demo' | 'influencias' | 'negocios';
  readonly badgeTitle: string;
  readonly bodyText: string;
  readonly ctaPrompt: string;
  readonly tablerIcon: string;
}

/** Specs FIEIS ao objeto `data` de _ensureInsights (1092-1097). Sem aba 'visao'/'pertencimento'. */
const PERFIL_INSIGHT_CARDS: readonly PerfilInsightCardSpec[] = [
  {
    panelKey: 'eleicoes',
    badgeTitle: 'Insight eleitoral',
    bodyText:
      'Avante saiu de 24% em 2020 para 52% em 2024 na Zona Norte. O salto ocorreu junto com queda do MDB, criando disputa direta com PL no voto antipetista.',
    ctaPrompt: 'Explicar a virada eleitoral da Zona Norte',
    tablerIcon: 'ti-vote',
  },
  {
    panelKey: 'demo',
    badgeTitle: 'Insight demográfico',
    bodyText:
      'Zona Norte é jovem, feminina e de renda média-baixa. A combinação favorece mensagens de saúde, emprego local e segurança de bairro.',
    ctaPrompt: 'Gerar estratégia demográfica para Zona Norte',
    tablerIcon: 'ti-users',
  },
  {
    panelKey: 'influencias',
    badgeTitle: 'Insight de influência',
    bodyText:
      'A rede religiosa tem menor alcance bruto que a política, mas maior capilaridade de bairro. Lideranças comunitárias funcionam como pontes entre igrejas e comércio local.',
    ctaPrompt: 'Mapear pontes de influência na Zona Norte',
    tablerIcon: 'ti-affiliate',
  },
  {
    panelKey: 'negocios',
    badgeTitle: 'Insight econômico',
    bodyText:
      'Indústria e varejo sustentam emprego formal local. O cruzamento com doações indica que grupos econômicos podem amplificar agendas de renda e mobilidade.',
    ctaPrompt: 'Cruzar negócios da Zona Norte com votação',
    tablerIcon: 'ti-building-store',
  },
];

/**
 * Constroi o HTML do card de insight (replica UI.renderInsightCard, 848-855).
 * Escapa aspas simples do prompt para o onclick inline, identico ao original.
 */
function renderPerfilInsightCard(card: PerfilInsightCardSpec): string {
  const escapedPrompt = card.ctaPrompt.replace(/'/g, "\\'");
  return `<div class="u-insight-card">` +
    `<div class="badge">${card.badgeTitle}</div>` +
    `<div class="insight-text">${card.bodyText}</div>` +
    `<div class="cta" onclick="sendPrompt('${escapedPrompt}')">` +
    `<i class="ti ${card.tablerIcon}" style="font-size:11px"></i> análise completa <i class="ti ti-arrow-up-right" style="font-size:10px" aria-hidden="true"></i></div>` +
    `</div>`;
}

/**
 * Injeta os cards de insight no fim dos paineis correspondentes, uma unica vez.
 * Espelha _ensureInsights: pula paineis ausentes; retorna se ja injetado.
 * @param alreadyInjected flag de estado do controller (insightsInjected).
 * @returns novo valor da flag — `true` apos injetar, `false` se nada feito.
 */
export function ensurePerfilInsights(alreadyInjected: boolean): boolean {
  if (alreadyInjected) return true;
  PERFIL_INSIGHT_CARDS.forEach((card) => {
    const panel = document.querySelector(`.u-perfil-panel[data-panel="${card.panelKey}"]`);
    if (!panel) return;
    panel.insertAdjacentHTML('beforeend', renderPerfilInsightCard(card));
  });
  return true;
}
